/* eslint-disable no-case-declarations */
import { useForm } from 'react-hook-form'
import * as Redux from 'redux'
import { connect } from 'react-redux'
import ClientFirearmAnomalyActions from '@ClientActions/firearm-anomaly.action'
import { useEffect, useState } from 'react'
import { MFirearmAnomalyResolution } from '@Models/client/firearm-anomaly-resolution.model'
import {
    ICommonQuery,
    IMetaData,
    IPageHeaderActions,
    ITableHeaders,
} from '@Models/common.model'
import { APIServices } from '@Services/api.service'
import { CActions } from '@Constants/common.constant'
import PageHeader from '@Components/Pages/PageHeader/PageHeader'
import AnomalyForm from '@Pages/Client/firearms-anomaly-resolution/anomalyForm'
import BaseTable from '@Components/table/BaseTable/BaseTable'
import moment from 'moment'
import { AxiosResponse } from 'axios'
import BasePagination, {
    IBasePaginationEvent,
} from '@Components/table/BasePagination/BasePagination'

// dummy data
const defaultValues: any = {
    madeAt: null,
    issue: '',
    ref: '',
    anomaly: '',
}

const headers: ITableHeaders[] = [
    {
        name: 'No',
        key: 'no',
        formatType: 'no',
    },
    {
        name: 'Date',
        key: 'madeAt',
        formatType: 'date',
    },
    {
        name: 'Issue',
        key: 'issue',
    },
    {
        name: 'Ref #',
        key: 'ref',
    },
    {
        name: 'Status',
        key: 'status',
    },
]

const submitAction: IPageHeaderActions = {
    ...CActions.save,
    name: 'Submit',
}

// models section
interface IFirearmAnomalies {
    // redux
    getFirearmAnomalies: () => void
    createFirearmAnomaly: (body: any) => Promise<any>
    getRefGenerated: () => Promise<any>
    setQueries: (params: ICommonQuery) => void
    firearmAnomalies: MFirearmAnomalyResolution[]
    metadata: IMetaData
    isLoading: boolean
    firearmAnomalyQueries: ICommonQuery
}

function FirearmsAnomalyResolution(props: IFirearmAnomalies) {
    // core section
    const {
        getFirearmAnomalies,
        createFirearmAnomaly,
        getRefGenerated,
        setQueries,
        firearmAnomalies,
        metadata,
        isLoading,
        firearmAnomalyQueries,
    } = props
    const {
        control,
        handleSubmit,
        formState: { errors },
        reset,
        setValue,
    } = useForm({ defaultValues })

    // states section
    const [currentPage, setCurrentPage] = useState(1)
    const [headerActions, setHeaderActions] = useState<IPageHeaderActions[]>([
        submitAction,
    ])

    /**
     * reset form fields (madeAt, ref)
     */
    const resetFormFields = () => {
        setValue('madeAt', moment().toISOString())
        getRefGenerated().then((response: AxiosResponse) => {
            const code = response?.data?.code
            setValue('ref', code)
        })
    }

    // hook section
    /**
     * getting data from API when first load
     */
    useEffect(() => {
        getFirearmAnomalies()
        resetFormFields()
        return () => {
            APIServices.axiosCancelToken()
        }
    }, [])

    /**
     * handle submit and create/update firearm anomaly
     * @param formData get from form inputs
     */
    const onSubmit = (formData: any) => {
        setHeaderActions([{ ...submitAction, isLoading: true, disabled: true }])
        const madeAt = formData?.madeAt
            ? moment(formData?.madeAt).toISOString()
            : ''
        const body = { ...formData, madeAt }

        createFirearmAnomaly(body)
            .then(() => {
                // get data to latest page
                // const { limit, total, offset } = metadata
                // let newOffset = 10
                // let newPage = 1
                // const quotient =
                //     Number((total && total + 1) || 0) / Number(limit || 0)
                // if (quotient > 1) {
                //     newPage = Math.ceil(quotient)
                //     newOffset = ((limit && +limit) || 10) * (newPage - 1)
                // } else {
                //     newOffset = (offset && offset) || 0
                // }

                setHeaderActions([
                    { ...submitAction, isLoading: false, disabled: false },
                ])
                reset(defaultValues)
                resetFormFields()
                setQueries({ ...firearmAnomalyQueries, offset: 0 })
                getFirearmAnomalies()
                // setCurrentPage(quotient > 1 ? newPage : 1)
                setCurrentPage(1)
            })
            .catch(() =>
                setHeaderActions([
                    { ...submitAction, isLoading: false, disabled: false },
                ])
            )
    }

    /**
     * handle event click by action value
     * @param action action value from header component
     */
    const handleHeaderActions = (action?: string) => {
        switch (action) {
            case 'save':
                handleSubmit(onSubmit)()
                break
            default:
                break
        }
    }

    const handlePageChange = (evt: IBasePaginationEvent) => {
        const { offset, limit } = evt
        if (setQueries) setQueries({ limit, offset })
        if (getFirearmAnomalies) getFirearmAnomalies()
    }

    // main section
    return (
        <div className="firearm-anomaly-resolution-page">
            <div className="page-min-height">
                <PageHeader
                    title="Firearms Anomaly & Resolution"
                    actions={headerActions}
                    getActions={handleHeaderActions}
                />
                <form onSubmit={handleSubmit(onSubmit)}>
                    <AnomalyForm control={control} errors={errors} />
                </form>
                <div className="mt-2">
                    <p className="page-title mt-4">
                        Firearms Anomaly & Resolution
                    </p>
                    <BaseTable
                        headers={headers}
                        data={firearmAnomalies}
                        metadata={metadata}
                        isLoading={isLoading}
                    />
                    <BasePagination
                        currentPage={currentPage}
                        metadata={metadata}
                        pageChanged={handlePageChange}
                    />
                </div>
            </div>
        </div>
    )
}

// store section
const mapStateToProps = (store: any) => {
    const { register, user } = store?.auth
    const { firearmAnomalies, metadata, isLoading, firearmAnomalyQueries } =
        store?.client?.firearmAnomaly
    return {
        register,
        user,
        firearmAnomalies,
        metadata,
        isLoading,
        firearmAnomalyQueries,
    }
}

const mapDispatchToProps = (dispatch: Redux.Dispatch) => {
    const {
        getFirearmAnomalies,
        createFirearmAnomaly,
        updateFirearmAnomalies,
        getRefGenerated,
        setFirearmAnomalyQueries,
    } = ClientFirearmAnomalyActions
    return {
        getFirearmAnomalies: () => dispatch(getFirearmAnomalies() as any),
        createFirearmAnomaly: (body: any) =>
            dispatch(createFirearmAnomaly(body) as any),
        updateFirearmAnomalies: (body: any) =>
            dispatch(updateFirearmAnomalies(body) as any),
        getRefGenerated: () => dispatch(getRefGenerated() as any),
        setQueries: (params: ICommonQuery) =>
            dispatch(setFirearmAnomalyQueries(params) as any),
    }
}

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(FirearmsAnomalyResolution)
